/**
 * Wholesale Orders API Client
 * Phase 2: Wholesale Partnership Module
 */

import apiClient from '../client';

const BASE_URL = '/wholesale/orders';

// ===== LIST & SEARCH =====

/**
 * Get all orders with filters and pagination
 */
export const getOrders = async (params = {}) => {
  try {
    const response = await apiClient.get(BASE_URL, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch orders',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Get single order by ID
 */
export const getOrderById = async (id) => {
  try {
    const response = await apiClient.get(`${BASE_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching order:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch order',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Get pending orders
 */
export const getPendingOrders = async () => {
  try {
    const response = await apiClient.get(`${BASE_URL}/pending`);
    return response.data;
  } catch (error) {
    console.error('Error fetching pending orders:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch pending orders',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

// ===== CREATE ORDERS =====

/**
 * Create a new order manually
 */
export const createOrder = async (data) => {
  try {
    const response = await apiClient.post(BASE_URL, data);
    return response.data;
  } catch (error) {
    console.error('Error creating order:', error);
    throw {
      message: error.response?.data?.error || 'Failed to create order',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Create order from WhatsApp message
 */
export const createOrderFromWhatsApp = async (whatsappOrderId, parsedData) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/from-whatsapp`, {
      whatsapp_order_id: whatsappOrderId,
      ...parsedData
    });
    return response.data;
  } catch (error) {
    console.error('Error creating order from WhatsApp:', error);
    throw {
      message: error.response?.data?.error || 'Failed to create order from WhatsApp',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Update an existing order (before loading status)
 */
export const updateOrder = async (id, data) => {
  try {
    const response = await apiClient.patch(`${BASE_URL}/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating order:', error);
    throw {
      message: error.response?.data?.error || 'Failed to update order',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

// ===== ORDER WORKFLOW =====

/**
 * Update order status
 */
export const updateOrderStatus = async (id, status, notes = null) => {
  try {
    const response = await apiClient.patch(`${BASE_URL}/${id}/status`, { status, notes });
    return response.data;
  } catch (error) {
    console.error('Error updating order status:', error);
    throw {
      message: error.response?.data?.error || 'Failed to update order status',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Forward order to manufacturer
 * @param {number} id - Order ID
 * @param {number} manufacturingCompanyId - Manufacturing company ID to forward to
 */
export const forwardOrderToManufacturer = async (id, manufacturingCompanyId) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${id}/forward`, {
      manufacturing_company_id: manufacturingCompanyId
    });
    return response.data;
  } catch (error) {
    console.error('Error forwarding order to manufacturer:', error);
    throw {
      message: error.response?.data?.error || 'Failed to forward order to manufacturer',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Edit order after forwarding (item replacement)
 */
export const editOrderAfterForwarding = async (id, changes) => {
  try {
    const response = await apiClient.patch(`${BASE_URL}/${id}/edit-after-forwarding`, changes);
    return response.data;
  } catch (error) {
    console.error('Error editing order after forwarding:', error);
    throw {
      message: error.response?.data?.error || 'Failed to edit order after forwarding',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

// ===== WAYBILLS =====

/**
 * Get waybills for an order
 */
export const getOrderWaybills = async (orderId) => {
  try {
    const response = await apiClient.get(`${BASE_URL}/${orderId}/waybills`);
    return response.data;
  } catch (error) {
    console.error('Error fetching order waybills:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch order waybills',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

// ===== PAYMENT & INVOICING =====

/**
 * Generate proforma invoice for an order
 */
export const generateProformaInvoice = async (orderId) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${orderId}/proforma-invoice`);
    return response.data;
  } catch (error) {
    console.error('Error generating proforma invoice:', error);
    throw {
      message: error.response?.data?.error || 'Failed to generate proforma invoice',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Regenerate proforma invoice for an order
 */
export const regenerateProformaInvoice = async (orderId) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${orderId}/proforma-invoice/regenerate`);
    return response.data;
  } catch (error) {
    console.error('Error regenerating proforma invoice:', error);
    throw {
      message: error.response?.data?.error || 'Failed to regenerate proforma invoice',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Send proforma invoice to customer via WhatsApp
 */
export const sendProformaToWhatsApp = async (orderId, data = {}) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${orderId}/proforma-invoice/send-whatsapp`, data);
    return response.data;
  } catch (error) {
    console.error('Error sending proforma to WhatsApp:', error);
    throw {
      message: error.response?.data?.error || 'Failed to send proforma to WhatsApp',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Record payment for an order
 */
export const recordOrderPayment = async (orderId, paymentData) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${orderId}/payment`, paymentData);
    return response.data;
  } catch (error) {
    console.error('Error recording payment:', error);
    throw {
      message: error.response?.data?.error || 'Failed to record payment',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Generate documents when order is loaded (invoice/receipt)
 */
export const generateLoadedDocuments = async (orderId) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${orderId}/generate-documents`);
    return response.data;
  } catch (error) {
    console.error('Error generating loaded documents:', error);
    throw {
      message: error.response?.data?.error || 'Failed to generate documents',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Mark order as credit (outstanding payment)
 * Sets payment_status to 'credit' and generates invoice
 */
export const markOrderAsCredit = async (orderId, notes = '') => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${orderId}/mark-as-credit`, { notes });
    return response.data;
  } catch (error) {
    console.error('Error marking order as credit:', error);
    throw {
      message: error.response?.data?.error || 'Failed to mark order as credit',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Confirm document review
 * Marks that the user has reviewed and confirmed the invoice/receipt
 */
export const confirmDocumentReview = async (orderId) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${orderId}/confirm-document-review`);
    return response.data;
  } catch (error) {
    console.error('Error confirming document review:', error);
    throw {
      message: error.response?.data?.error || 'Failed to confirm document review',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

export default {
  getOrders,
  getOrderById,
  getPendingOrders,
  createOrder,
  createOrderFromWhatsApp,
  updateOrderStatus,
  forwardOrderToManufacturer,
  editOrderAfterForwarding,
  getOrderWaybills,
  generateProformaInvoice,
  regenerateProformaInvoice,
  sendProformaToWhatsApp,
  recordOrderPayment,
  generateLoadedDocuments,
  markOrderAsCredit
};
